import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const LearningGoalCard = ({ goal, onUpdate, onDelete }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editedGoal, setEditedGoal] = useState(goal);

  const handleSave = () => {
    onUpdate(editedGoal);
    setIsEditing(false);
  };

  const handleCancel = () => {
    setEditedGoal(goal);
    setIsEditing(false);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'text-success bg-success/10';
      case 'in-progress':
        return 'text-secondary bg-secondary/10';
      case 'pending':
        return 'text-muted-foreground bg-muted';
      default:
        return 'text-muted-foreground bg-muted';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'completed':
        return 'Concluído';
      case 'in-progress':
        return 'Em Progresso';
      case 'pending':
        return 'Pendente';
      default:
        return 'Pendente';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high':
        return 'text-error';
      case 'medium':
        return 'text-warning';
      case 'low':
        return 'text-muted-foreground';
      default:
        return 'text-muted-foreground';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('pt-BR');
  };

  const calculateDaysRemaining = (targetDate) => {
    const today = new Date();
    const target = new Date(targetDate);
    const diffTime = target - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  if (isEditing) {
    return (
      <div className="bg-card rounded-lg border border-border p-6">
        <div className="space-y-4">
          <Input
            label="Título da Meta"
            value={editedGoal?.title}
            onChange={(e) => setEditedGoal({ ...editedGoal, title: e?.target?.value })}
            placeholder="Digite o título da meta"
          />
          
          <Input
            label="Descrição"
            value={editedGoal?.description}
            onChange={(e) => setEditedGoal({ ...editedGoal, description: e?.target?.value })}
            placeholder="Descreva sua meta de aprendizado"
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Data Alvo"
              type="date"
              value={editedGoal?.targetDate}
              onChange={(e) => setEditedGoal({ ...editedGoal, targetDate: e?.target?.value })}
            />
            
            <Input
              label="Progresso Atual"
              type="number"
              value={editedGoal?.currentProgress}
              onChange={(e) => setEditedGoal({ ...editedGoal, currentProgress: parseInt(e?.target?.value) })}
              min="0"
              max={editedGoal?.targetValue}
            />
          </div>

          <div className="flex items-center justify-end space-x-3">
            <Button variant="outline" onClick={handleCancel}>
              Cancelar
            </Button>
            <Button variant="default" onClick={handleSave}>
              Salvar
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const progressPercentage = (goal?.currentProgress / goal?.targetValue) * 100;
  const daysRemaining = calculateDaysRemaining(goal?.targetDate);

  return (
    <div className="bg-card rounded-lg border border-border p-6 hover:shadow-tier-2 transition-micro">
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1">
          <div className="flex items-center space-x-3 mb-2">
            <h3 className="text-lg font-heading font-semibold text-foreground">
              {goal?.title}
            </h3>
            <div className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(goal?.status)}`}>
              {getStatusLabel(goal?.status)}
            </div>
          </div>
          <p className="text-sm text-muted-foreground font-caption mb-3">
            {goal?.description}
          </p>
        </div>
        
        <div className="flex items-center space-x-2">
          <Icon 
            name="Flag" 
            size={16} 
            className={getPriorityColor(goal?.priority)} 
          />
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsEditing(true)}
            iconName="Edit"
            iconSize={16}
          />
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onDelete(goal?.id)}
            iconName="Trash2"
            iconSize={16}
            className="text-error hover:text-error"
          />
        </div>
      </div>
      {/* Progress Section */}
      <div className="mb-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-foreground">Progresso</span>
          <span className="text-sm text-muted-foreground font-caption">
            {goal?.currentProgress}/{goal?.targetValue} {goal?.unit}
          </span>
        </div>
        <div className="w-full bg-muted rounded-full h-3">
          <div
            className="bg-gradient-to-r from-secondary to-primary h-3 rounded-full transition-all duration-500"
            style={{ width: `${Math.min(progressPercentage, 100)}%` }}
          />
        </div>
        <div className="flex items-center justify-between mt-2">
          <span className="text-xs text-muted-foreground font-caption">
            {progressPercentage?.toFixed(1)}% concluído
          </span>
          {goal?.status !== 'completed' && (
            <span className={`text-xs font-caption ${
              daysRemaining < 0 ? 'text-error' : daysRemaining < 7 ? 'text-warning' : 'text-muted-foreground'
            }`}>
              {daysRemaining < 0 
                ? `${Math.abs(daysRemaining)} dias atrasado`
                : daysRemaining === 0 
                ? 'Vence hoje'
                : `${daysRemaining} dias restantes`
              }
            </span>
          )}
        </div>
      </div>
      {/* Goal Details */}
      <div className="grid grid-cols-2 gap-4 pt-4 border-t border-border">
        <div className="flex items-center space-x-2">
          <Icon name="Calendar" size={16} className="text-muted-foreground" />
          <div>
            <p className="text-xs text-muted-foreground font-caption">Data Alvo</p>
            <p className="text-sm font-medium text-foreground">
              {formatDate(goal?.targetDate)}
            </p>
          </div>
        </div>
        
        <div className="flex items-center space-x-2">
          <Icon name="Target" size={16} className="text-muted-foreground" />
          <div>
            <p className="text-xs text-muted-foreground font-caption">Categoria</p>
            <p className="text-sm font-medium text-foreground capitalize">
              {goal?.category}
            </p>
          </div>
        </div>
      </div>
      {/* Action Buttons */}
      {goal?.status !== 'completed' && (
        <div className="mt-4 pt-4 border-t border-border">
          <Button
            variant="outline"
            size="sm"
            fullWidth
            iconName="Plus"
            iconPosition="left"
            iconSize={16}
            onClick={() => {
              const newProgress = Math.min(goal?.currentProgress + 1, goal?.targetValue);
              onUpdate({ ...goal, currentProgress: newProgress });
            }}
          >
            Atualizar Progresso
          </Button>
        </div>
      )}
    </div>
  );
};

export default LearningGoalCard;